import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
    Typography
} from '@mui/material';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import { StaffService } from '../api/services/staffService';

const NOTE_TYPES = ['Commendation', 'Warning', 'General Feedback', 'Improvement Plan', 'Disciplinary', 'Other'];

const defaultState = {
    type: 'General Feedback',
    content: '',
    date: new Date().toISOString().slice(0, 16)  // YYYY-MM-DDTHH:mm for datetime-local
};

export default function AddNoteModal({ open, onClose, onSuccess, staffId, staffName }) {
    const [formData, setFormData] = useState(defaultState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (open) {
            setFormData(defaultState);
            setErrors({});
            setSubmitError('');
        }
    }, [open]);

    const validate = () => {
        const newErrors = {};
        if (!formData.type) newErrors.type = 'Note type is required';
        if (!formData.content.trim()) newErrors.content = 'Note content is required';
        if (!formData.date) newErrors.date = 'Date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const payload = {
                type: formData.type,
                content: formData.content.trim(),
                date: new Date(formData.date).toISOString()
            };

            await StaffService.addStaffNote(staffId, payload);
            onSuccess('Performance note added successfully');
            onClose();
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to add performance note.';
            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NoteAltIcon color="primary" />
                Add Performance Note
                {staffName && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontWeight: 400 }}>
                        for {staffName}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers>
                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <FormControl required error={!!errors.type} disabled={isSubmitting}>
                        <InputLabel>Note Type</InputLabel>
                        <Select
                            value={formData.type}
                            label="Note Type"
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        >
                            {NOTE_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Note Content"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        multiline
                        rows={5}
                        placeholder="Describe the performance observation, commendation, or concern in detail..."
                        error={!!errors.content}
                        helperText={errors.content}
                        required
                        disabled={isSubmitting}
                    />

                    <TextField
                        type="datetime-local"
                        label="Date & Time"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.date}
                        helperText={errors.date || 'Defaults to current time'}
                        required
                        disabled={isSubmitting}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <NoteAltIcon />}
                >
                    {isSubmitting ? 'Saving...' : 'Add Note'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
